import { FormattedMessage } from 'react-intl';
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Label,
  ReadOnly,
  useDataProvider,
  useSnackbar,
  SnackbarApiErrorMessage,
  useLocalization,
  DATE_FORMAT,
} from '@ui-framework/index';
import dayjs from 'dayjs';
import Currency from '../../../Currency';

function formatDate(dateString) {
  return dateString ? dayjs(dateString).format(DATE_FORMAT.LONG_DATE) : '';
}

function TotalPaidField({ currentvalue }) {
  return <Currency>{currentvalue}</Currency>;
}

function ClaimPaymentDetails({ serviceJobId, claimId }) {
  const { formatMessage } = useLocalization();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, get } = useDataProvider(provider);
  const snackbar = useSnackbar();

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await get(
          `${getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/claims/${claimId}/payment-details`,
        );
        if (response.ok) {
          setPaymentDetails(response?.data?.data || null);
        } else {
          snackbar.showMessage({
            type: 'error',
            data: <SnackbarApiErrorMessage errors={response?.errors} />,
          });
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPaymentDetails();
  }, [serviceJobId, claimId]);

  if (error)
    return (
      <div>
        <FormattedMessage
          id="ERROR_GETTING_PAYMENT_DETAILS"
          defaultMessage="Error Getting Payment Details"
        />
      </div>
    );
  if (!paymentDetails || paymentDetails.length === 0)
    return (
      <div>
        <FormattedMessage
          id="NO_PAYMENT_DETAILS_FOUND"
          defaultMessage="No payment details found."
        />
      </div>
    );

  const headers = [
    {
      key: 'paymentReferenceNumber',
      label: formatMessage({
        id: 'REFERENCE_NUMBER',
        defaultMessage: 'Reference Number',
      }),
    },
    {
      key: 'paymentStatus',
      label: formatMessage({
        id: 'PAYMENT_STATUS',
        defaultMessage: 'Payment Status',
      }),
    },
    {
      key: 'paymentMethod',
      label: formatMessage({
        id: 'PAYMENT_METHOD',
        defaultMessage: 'Payment Method',
      }),
    },
    {
      key: 'totalPaid',
      label: formatMessage({
        id: 'TOTAL_PAID',
        defaultMessage: 'Total Paid',
      }),
      component: TotalPaidField,
    },
    {
      key: 'paymentDate',
      label: formatMessage({
        id: 'PAYMENT_DATE',
        defaultMessage: 'Payment Date',
      }),
      transform: value => formatDate(value),
    },
    {
      key: 'createdDateTime',
      label: formatMessage({
        id: 'CREATED',
        defaultMessage: 'Created',
      }),
      transform: value => formatDate(value),
    },
    {
      key: 'updatedDateTime',
      label: formatMessage({
        id: 'UPDATED',
        defaultMessage: 'Updated',
      }),
      transform: value => formatDate(value),
    },
    {
      key: 'paymentNotes',
      label: formatMessage({
        id: 'NOTES',
        defaultMessage: 'Notes',
      }),
    },
  ];

  return (
    <Box>
      {loading ? (
        <Box textAlign="center">
          <CircularProgress size="30px" />
          <Typography>
            <FormattedMessage id="PROCESSING" defaultMessage="Processing" />
          </Typography>
        </Box>
      ) : (
        // Render payment details header on single row, then details below
        <Box>
          <Grid2
            container
            sx={{
              mb: 1,
            }}>
            {headers.map(header => (
              <Grid2 key={header.key} xs={1.5}>
                <Label style={{ display: 'block', marginBottom: '5px' }}>
                  {header.label}
                </Label>
              </Grid2>
            ))}
          </Grid2>
          <Grid2>
            {paymentDetails.map(payment => (
              <Grid2 container key={payment.id} sx={{ mb: 1 }}>
                {headers.map(header => (
                  <Grid2 key={header.key} xs={1.5}>
                    <ReadOnly>
                      {(() => {
                        if (header.component) {
                          return (
                            <header.component
                              currentvalue={payment[header.key]}
                            />
                          );
                        }
                        if (header.transform) {
                          return header.transform(payment[header.key]);
                        }
                        return payment[header.key];
                      })()}
                    </ReadOnly>
                  </Grid2>
                ))}
              </Grid2>
            ))}
          </Grid2>
        </Box>
      )}
    </Box>
  );
}

export default ClaimPaymentDetails;
